import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import VideoCard from '@/components/feature/VideoCard';
import CategoryNav from '@/components/feature/CategoryNav';
import SchemaOrg from '@/components/base/SchemaOrg';
import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/navigation';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';

  const activeSlug = slug || 'all';
  const [currentCategory, setCurrentCategory] = useState(activeSlug);

  useEffect(() => {
    setCurrentCategory(activeSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const categoryName = useMemo(() => {
    const cat = categories.find((c) => c.slug === currentCategory);
    return cat ? cat.name : '全部';
  }, [currentCategory]);

  const filteredVideos = useMemo(() => {
    let result = videos;

    if (currentCategory !== 'all') {
      result = result.filter((v) => v.category === currentCategory);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.description.toLowerCase().includes(q) ||
          v.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    return result;
  }, [currentCategory, searchQuery]);

  const breadcrumbs = [
    { name: '首页', url: '/' },
    { name: categoryName, url: currentCategory === 'all' ? '/' : `/category/${currentCategory}` },
  ];

  return (
    <Layout>
      <SchemaOrg
        type="BreadcrumbList"
        breadcrumbs={breadcrumbs}
      />
      <SchemaOrg type="WebSite" />

      <section className="pt-24 pb-12 md:pb-16">
        <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">{categoryName}</span>
          </nav>

          <div className="mb-8">
            <CategoryNav
              activeCategory={currentCategory}
              useLink={true}
            />
          </div>

          {searchQuery && (
            <div className="mb-6 p-4 rounded-xl bg-background-100 border border-background-200">
              <p className="text-sm text-foreground-600">
                搜索 "<strong className="text-foreground-900">{searchQuery}</strong>" 的结果，
                共找到 <strong className="text-primary-600">{filteredVideos.length}</strong> 个视频
              </p>
            </div>
          )}

          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-video-off-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="text-lg font-medium text-foreground-700 mb-2">暂无相关视频</h3>
              <p className="text-sm text-foreground-500">换个分类或搜索词试试吧</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}